import moment from "moment";
import { Modal } from "antd";

import {
  buildShipmentDataFromListResponse,
  buildTelemetryInfoPatchFromListResponse,
  buildAllDetailState,
} from "../utils/shipmentsTransformers";

const POD_UNAVAILABLE_MODAL = {
  title: "No disponible",
  content: "La imagen de firma (POD) no está disponible para su descarga.",
};

const prepareArrayParam = (arr) =>
  Array.isArray(arr) && arr.length > 0 ? arr.join(",") : null;

export const buildShipmentsQueryParams = ({
  shipmentCode,
  imei,
  numRefClient,
  start,
  end,
  clientNumber,
  labellerCode,
  boxType,
  temperatureTrazability,
  temperatureRange,
  size = 10,
  page = 1,
}) => ({
  shipmentCode,
  imei,
  numRefClient,
  startDate: start ? moment(start).format("DD-MM-YYYY") : null,
  endDate: end ? moment(end).format("DD-MM-YYYY") : null,
  clientNumber,
  labellerCode,
  boxType: prepareArrayParam(boxType),
  temperatureTrazability: prepareArrayParam(temperatureTrazability),
  temperatureRange: prepareArrayParam(temperatureRange),
  size,
  page: page - 1,
});

export const generatePod = (shipmentCode, pod) => {
  const linkSource = `data:image/jpeg;base64,${pod}`;
  const downloadLink = document.createElement("a");
  const fileName = `${shipmentCode}.jpg`;
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
};

function applyTelemetryInfoPatchFromList(response, setTelemetryInfo) {
  const patch = buildTelemetryInfoPatchFromListResponse(response.data);
  if (patch) {
    setTelemetryInfo((prev) => ({
      ...prev,
      full: {
        ...prev.full,
        warnings: patch.full.warnings,
      },
      reduced: {
        ...prev.reduced,
        warnings: patch.reduced.warnings,
      },
      statusCode: patch.statusCode,
    }));
  }
}

function applySingleShipmentNotFound(response, setSingleShipmentData, setEventsInfo) {
  setSingleShipmentData((prevState) => ({
    ...prevState,
    statusCode: response.status,
    status: "loaded",
    message: "No se han encontrado datos de envío.",
  }));

  setEventsInfo((prevState) => ({
    ...prevState,
    statusCode: response.status,
  }));
}

function applySingleShipmentSuccess(
  data,
  responseStatus,
  {
    setSingleShipmentData,
    setBatteryLevels,
    setTelemetryInfo,
    setAlertsListInfo,
    setChartsInfo,
    setEventsInfo,
  },
) {
  const computed = buildAllDetailState(data, responseStatus);

  setSingleShipmentData(computed.singleShipmentData);

  if (typeof setBatteryLevels === "function") {
    setBatteryLevels(computed.batteryLevels);
  }

  if (computed.telemetryInfoPatch) {
    setTelemetryInfo((prev) => ({
      ...prev,
      ...computed.telemetryInfoPatch,
    }));
  }

  setAlertsListInfo(computed.alertsListInfo);

  setChartsInfo((prevState) => ({
    ...prevState,
    ...computed.chartsInfo,
    statusCode: responseStatus,
  }));

  setEventsInfo((prevState) => ({
    ...prevState,
    ...computed.eventsInfo,
    statusCode: 200,
  }));
}

function applySingleShipmentFailure(
  setSingleShipmentData,
  setEventsInfo,
  setTelemetryInfo,
  error,
) {
  setSingleShipmentData((prevState) => ({
    ...prevState,
    statusCode: 500,
    message: "Se ha producido un error, inténtelo de nuevo.",
  }));

  setEventsInfo((prevState) => ({
    ...prevState,
    statusCode: 500,
    message: "",
  }));

  setTelemetryInfo((prevState) => ({
    ...prevState,
    statusCode: 500,
    message: "No se han encontrado datos de alertas para este envío",
  }));

  console.log(error);
}

export async function fetchShipmentsList(
  instance,
  back,
  setShipmentData,
  setTelemetryInfo,
  {
    shipmentCode,
    imei,
    numRefClient,
    start,
    end,
    clientNumber,
    labellerCode,
    boxType,
    temperatureTrazability,
    temperatureRange,
    size = 10,
    page = 1,
    pagination = false,
  },
) {
  const isPaginating = Boolean(pagination);
  const nextStatus = isPaginating ? "paginating" : "loading";

  setShipmentData((prev) => ({
    ...prev,
    status: nextStatus,
    message: "Cargando...",
    statusCode: 0,
  }));

  try {
    const response = await instance.get(back.getListEnvios, {
      params: buildShipmentsQueryParams({
        shipmentCode,
        imei,
        numRefClient,
        start,
        end,
        clientNumber,
        labellerCode,
        boxType,
        temperatureTrazability,
        temperatureRange,
        size,
        page,
      }),
    });

    const { shipmentDataState, responseOk } =
      buildShipmentDataFromListResponse(response, size, page);

    setShipmentData(shipmentDataState);

    if (responseOk) {
      applyTelemetryInfoPatchFromList(response, setTelemetryInfo);
    }

    return {
      statusCode: shipmentDataState.statusCode,
      data: shipmentDataState.data || [],
      totalResults: shipmentDataState.totalResults || 0,
      totalPages: shipmentDataState.totalPages || 0,
      page: shipmentDataState.page || page,
      size: shipmentDataState.size || size,
      message: shipmentDataState.message || "",
    };
  } catch {
    const errorResponse = {
      statusCode: 500,
      data: [],
      message: "Se ha producido un error, inténtelo de nuevo.",
      page: 1,
      totalResults: 0,
      totalPages: 0,
    };

    setShipmentData((prev) => ({
      ...prev,
      status: "loaded",
      ...errorResponse,
    }));

    return errorResponse;
  }
}

export async function fetchShipmentsListForExport(instance, back, paramsObject) {
  const {
    shipmentCode,
    imei,
    numRefClient,
    start,
    end,
    clientNumber,
    labellerCode,
    boxType,
    temperatureTrazability,
    temperatureRange,
    maxSize = 500,
  } = paramsObject;

  try {
    const response = await instance.get(back.getListEnvios, {
      params: buildShipmentsQueryParams({
        shipmentCode,
        imei,
        numRefClient,
        start,
        end,
        clientNumber,
        labellerCode,
        boxType,
        temperatureTrazability,
        temperatureRange,
        size: maxSize,
        page: 1,
      }),
    });

    const { shipmentDataState } = buildShipmentDataFromListResponse(
      response,
      maxSize,
      1,
    );

    return {
      statusCode: shipmentDataState.statusCode,
      data: shipmentDataState.data || [],
      totalResults: shipmentDataState.totalResults || 0,
      totalPages: shipmentDataState.totalPages || 0,
      page: shipmentDataState.page || 1,
      size: shipmentDataState.size || maxSize,
      message: shipmentDataState.message || "",
    };
  } catch {
    return {
      statusCode: 500,
      data: [],
      message: "Se ha producido un error, inténtelo de nuevo.",
      totalResults: 0,
      totalPages: 0,
      page: 1,
      size: maxSize,
    };
  }
}

export async function fetchSingleShipmentDetail(
  instance,
  back,
  shipmentCode,
  singleShipmentSetters,
) {
  const url = back.getShipmentInfo;
  const {
    setSingleShipmentData,
    setBatteryLevels,
    setTelemetryInfo,
    setAlertsListInfo,
    setChartsInfo,
    setEventsInfo,
  } = singleShipmentSetters;

  try {
    const response = await instance.get(url, { params: { shipmentCode } });
    const { data } = response;

    if (response.status !== 200) {
      applySingleShipmentNotFound(response, setSingleShipmentData, setEventsInfo);
      return;
    }

    applySingleShipmentSuccess(data, response.status, {
      setSingleShipmentData,
      setBatteryLevels,
      setTelemetryInfo,
      setAlertsListInfo,
      setChartsInfo,
      setEventsInfo,
    });
  } catch (error) {
    applySingleShipmentFailure(
      setSingleShipmentData,
      setEventsInfo,
      setTelemetryInfo,
      error,
    );
  }
}

export function requestShipmentPod(instance, back, shipmentCode) {
  console.log(shipmentCode);
  instance
    .get(back.fetchShipmentPod, { params: { shipmentCode } })
    .then((response) => {
      if (response.status !== 200) {
        return Modal.error({
          title: POD_UNAVAILABLE_MODAL.title,
          content: POD_UNAVAILABLE_MODAL.content,
        });
      }
      const { shipmentCode, pod } = response.data;
      return Modal.success({
        title: `POD ${shipmentCode}`,
        content: "La imagen de firma se ha generado con éxito.",
        onOk: () => generatePod(shipmentCode, pod),
        okText: "Descargar",
      });
    })
    .catch((error) => {
      console.log(error);
      return Modal.error({
        title: POD_UNAVAILABLE_MODAL.title,
        content: POD_UNAVAILABLE_MODAL.content,
      });
    });
}

export function requestSearchOptions(instance, back, setSearchOptions) {
  instance
    .get(back.getSearchOptions)
    .then((response) => {
      if (response.status === 200) {
        setSearchOptions({
          data: response.data,
          status: "loaded",
          statusCode: 200,
          message: "Cargado",
        });
      }
    })
    .catch((error) => {
      console.log(error);
    });
}
